import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { profileHelpers } from '../lib/supabase';
import ProfileTypeSelector from './ProfileTypeSelector';
import { NAME_ANALYSIS_DOB_PLACEHOLDER, NAME_ANALYSIS_PLACE_PLACEHOLDER } from '../constants/nameAnalysis';

const API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

const inp = {
  width: '100%',
  padding: '0.55rem 0.75rem',
  border: '1.5px solid #fcd34d',
  borderRadius: '8px',
  fontSize: '0.88rem',
  outline: 'none',
  background: '#fffbeb',
  color: '#78350f',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

/**
 * EditProfileModal
 * Props:
 *   profile      — { id, name, dob, tob, place }
 *   userEmail    — string
 *   onClose      — () => void
 *   onSaved      — (updatedProfile) => void
 */
export default function EditProfileModal({ profile, userEmail, onClose, onSaved }) {
  const { language } = useLanguage();
  const [form, setForm]       = useState({ name: '', dob: '', tob: '', place: '', question: '' });
  const [profileMode, setProfileMode] = useState('complete');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (profile) {
      setForm({
        name:     profile.name     || '',
        dob:      profile.dob      || '',
        tob:      profile.tob      || '',
        place:    profile.place    || '',
        question: profile.question || '',
      });
      setProfileMode(profile.data?.profileType === 'name_analysis' ? 'name_analysis' : 'complete');
      setError(''); setSuccess('');
    }
  }, [profile]);

  if (!profile) return null;

  const isNameAnalysis = profileMode === 'name_analysis';
  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (!form.name) { setError('Name is required'); return; }
    if (!isNameAnalysis && (!form.dob || !form.place)) {
      setError('Name, date and place are required'); return;
    }

    setLoading(true);
    try {
      if (isNameAnalysis) {
        // Name Analysis mode — no birth details; (re)generate the analysis
        // for the (possibly edited) name and save via the existing,
        // unmodified update-full endpoint using neutral placeholders.
        const { data: analysis, error: previewErr } = await profileHelpers.previewNameAnalysis({ name: form.name });
        if (previewErr) throw new Error(previewErr.message || 'Could not analyse this name');

        const { data: updated, error: updErr } = await profileHelpers.updateProfileFull({
          id: profile.id,
          email: userEmail,
          name: form.name,
          dob: NAME_ANALYSIS_DOB_PLACEHOLDER,
          tob: null,
          place: NAME_ANALYSIS_PLACE_PLACEHOLDER,
          data: analysis,
        });
        if (updErr) throw new Error(updErr.message || 'Update failed');

        setSuccess('✅ Name Analysis updated!');
        setTimeout(() => {
          onSaved?.(updated || { ...profile, name: form.name, data: analysis });
          onClose?.();
        }, 1200);
        return;
      }

      // Complete Kundli mode — existing behaviour, unchanged.
      await axios.put(`${API_URL}/api/profiles/update-full`, {
        id:    profile.id,
        email: userEmail,
        name:  form.name,
        dob:   form.dob,
        tob:   form.tob,
        place: form.place,
      });

      const calcRes = await axios.post(`${API_URL}/api/calculate`, {
        name:  form.name,
        email: userEmail,
        date:  form.dob,
        time:  form.tob || '12:00',
        place: form.place,
        language,
      });
      const chartData = calcRes.data;

      await axios.put(`${API_URL}/api/profiles/update-full`, {
        id:    profile.id,
        email: userEmail,
        name:  form.name,
        dob:   form.dob,
        tob:   form.tob,
        place: form.place,
        data:  chartData,
      });

      setSuccess('✅ Profile updated & kundli recalculated!');
      setTimeout(() => {
        onSaved?.({ ...profile, ...form, data: chartData });
        onClose?.();
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ks-modal-scrim" style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <div style={{ background:'#ffffff', borderRadius:'16px', width:'100%', maxWidth:'420px', overflow:'hidden', boxShadow:'0 8px 30px rgba(0,0,0,0.06), 0 20px 50px rgba(0,0,0,0.1)', border:'1px solid rgb(254 215 170 / 0.45)' }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#78350f,#b45309)', padding:'1rem 1.25rem', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.5rem' }}>
          <div>
            <p style={{ margin:0, color:'rgba(255,255,255,.7)', fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'1px' }}>Edit Profile</p>
            <h3 style={{ margin:'0.15rem 0 0', color:'#fef3c7', fontWeight:800, fontSize:'1.05rem' }}>{profile.name}</h3>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,.15)', border:'none', color:'#fff', width:28, height:28, borderRadius:'50%', fontSize:'1.1rem', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding:'1.4rem', display:'flex', flexDirection:'column', gap:'0.85rem' }}>
          <ProfileTypeSelector value={profileMode} onChange={setProfileMode} compact />

          {[
            { label:'Full Name *',  field:'name',  type:'text',   placeholder:'e.g. Rahul Sharma' },
            ...(isNameAnalysis ? [] : [
              { label:'Birth Date *', field:'dob',   type:'date',   placeholder:'' },
              { label:'Birth Time',   field:'tob',   type:'time',   placeholder:'' },
              { label:'Birth Place *',field:'place', type:'text',   placeholder:'e.g. Mumbai, India' },
            ]),
          ].map(({ label, field, type, placeholder }) => (
            <div key={field}>
              <label style={{ display:'block', fontSize:'0.75rem', fontWeight:700, color:'#92400e', marginBottom:'0.3rem' }}>{label}</label>
              <input type={type} value={form[field]} placeholder={placeholder}
                onChange={set(field)} style={inp} />
            </div>
          ))}

          {isNameAnalysis && (
            <p style={{ margin:0, fontSize:'0.72rem', color:'#a16207', lineHeight:1.5 }}>
              You can always add birth details later to unlock your complete Kundli.
            </p>
          )}

          {!isNameAnalysis && form.question && (
            <div>
              <label style={{ display:'block', fontSize:'0.75rem', fontWeight:700, color:'#92400e', marginBottom:'0.3rem' }}>
                ❓ Their Question <span style={{ fontWeight:400, color:'#b45309' }}>(read-only)</span>
              </label>
              <textarea
                value={form.question}
                readOnly
                rows={3}
                style={{ ...inp, resize:'none', opacity:0.75, cursor:'default', color:'#78350f', lineHeight:1.5 }}
              />
            </div>
          )}

          {error   && <p style={{ margin:0, color:'#dc2626', fontSize:'0.8rem', background:'#fef2f2', padding:'0.5rem 0.75rem', borderRadius:'8px' }}>{error}</p>}
          {success && <p style={{ margin:0, color:'#16a34a', fontSize:'0.8rem', background:'#f0fdf4', padding:'0.5rem 0.75rem', borderRadius:'8px' }}>{success}</p>}

          <p style={{ margin:0, fontSize:'0.72rem', color:'#b45309' }}>
            {isNameAnalysis
              ? 'ℹ️ Saving will regenerate your Name Analysis insights.'
              : 'ℹ️ Saving will recalculate the kundli with the new details.'}
          </p>

          <div style={{ display:'flex', gap:'0.6rem' }}>
            <button type="button" onClick={onClose}
              style={{ flex:1, padding:'0.65rem', background:'#f3f4f6', border:'none', borderRadius:'10px', fontWeight:600, color:'#555', cursor:'pointer', fontSize:'0.88rem' }}>
              Cancel
            </button>
            <button type="submit" disabled={loading}
              style={{ flex:2, padding:'0.65rem', background: loading ? '#d1d5db' : 'linear-gradient(90deg,#78350f,#b45309)', color:'#fff', border:'none', borderRadius:'10px', fontWeight:700, fontSize:'0.9rem', cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? '⏳ Saving…' : (isNameAnalysis ? '💾 Save Name Analysis' : '💾 Save & Recalculate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
